import { Skeleton } from '@/components/ui/skeleton';
import { useHubLog, useHubLogs } from '@/services/hub.service';
import { useEffect, useMemo, useRef, useState } from 'react';
import { utcStamp } from './fieldTime';

// ---------------------------------------------------------------------------
// Log colorizing — ported from the standalone log-viewer.html so the hub logs
// render with the same highlighting (errors, start blocks, power events, …).
// ---------------------------------------------------------------------------

function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function colorLine(
    line: string,
    escaped: string,
    i: number,
    startBlockIndices: number[],
): string {
    if (/\|\s*ERROR\s*\|/.test(line))
        return `<span style="color:#ff6b6b;">${escaped}</span>`;
    if (/\|\s*WARNING\s*\|/.test(line))
        return `<span style="color:#ffa94d;">${escaped}</span>`;
    if (/={3,}.*START.*={3,}/.test(line)) {
        startBlockIndices.push(i);
        return `<span data-start="${i}" style="background:#1a4a1a;color:#6fcf6f;font-weight:bold;">${escaped}</span>`;
    }
    if (/={3,}.*CONFIG MODE.*={3,}/.test(line))
        return `<span style="background:#1a2a4a;color:#6fb0ff;font-weight:bold;">${escaped}</span>`;
    if (/={3,}.*DEFAULT MODE.*={3,}/.test(line))
        return `<span style="background:#2a2a4a;color:#b06fff;font-weight:bold;">${escaped}</span>`;
    if (/={3,}.*WITTYPI NEWLY POWERED MODE.*={3,}/.test(line))
        return `<span style="background:#2a2a4a;color:#b06fff;font-weight:bold;">${escaped}</span>`;
    if (/={3,}.*POWER OFF.*={3,}/.test(line))
        return `<span style="background:#4a1a1a;color:#ff6b6b;font-weight:bold;">${escaped}</span>`;
    if (/SIGTERM received/.test(line))
        return `<span style="background:#4a3a1a;color:#ffcc66;font-weight:bold;">${escaped}</span>`;
    if (/POWERING OFF/.test(line))
        return `<span style="background:#4a3a1a;color:#ffcc66;font-weight:bold;">${escaped}</span>`;
    if (/Next wake-up:/.test(line))
        return `<span style="color:#74c0fc;font-weight:bold;">${escaped}</span>`;
    if (/Startup reason:/.test(line))
        return `<span style="color:#a8d8a8;">${escaped}</span>`;
    if (/\|\s*Test\s+\S+:\s+PASS\b/.test(line))
        return escaped.replace('PASS', '✅ PASS');
    if (/\|\s*Test\s+\S+:\s+FAIL\b/.test(line))
        return escaped.replace('FAIL', '❌ FAIL');
    return escaped;
}

function buildHtml(content: string): {
    html: string;
    lines: string[];
    startBlockIndices: number[];
} {
    const lines = content.split('\n');
    const startBlockIndices: number[] = [];
    const html = lines
        .map((line, i) => colorLine(line, escapeHtml(line), i, startBlockIndices))
        .join('\n');
    return { html, lines, startBlockIndices };
}

/**
 * Split a hub log file name into its three parts, e.g.
 * `Scanorhize_2026-09-17_14-30-05.log` -> Scanorhize / 2026-09-17 / 14:30:05.
 * The time part is optional and accepts `-`, `:` or no separator. A name that
 * doesn't match keeps its raw form with empty date and time.
 */
function parseLogName(fileName: string): {
    name: string;
    date: string;
    time: string;
} {
    const m = fileName.match(
        /^(.*?)[_\-. ]?(\d{4}-\d{2}-\d{2})(?:[_T ](\d{2})[-:h]?(\d{2})(?:[-:m]?(\d{2}))?s?)?(?:\.[^.]+)?$/,
    );
    if (!m) return { name: fileName, date: '', time: '' };
    const [, name, date, hh, mm, ss] = m;
    const time = hh ? [hh, mm, ss].filter(Boolean).join(':') : '';
    return { name: name || fileName, date, time };
}

function formatSize(bytes: number): string {
    return `${(bytes / 1024).toFixed(1)} KB`;
}

export default function HubLogViewer({ macAddress }: { macAddress: string }) {
    const { data: files, isLoading, error } = useHubLogs(macAddress);
    const [selected, setSelected] = useState<string | null>(null);
    const [filter, setFilter] = useState('');

    // Auto-select the newest log once the list arrives (list is sorted
    // newest-first by the backend).
    useEffect(() => {
        if (!selected && files && files.length > 0) {
            setSelected(files[0].name);
        }
    }, [files, selected]);

    if (isLoading)
        return <Skeleton className="flex-1 min-h-0 bg-[#1e242a]" />;
    if (error)
        return <div className="text-red-400 text-sm">{error.toString()}</div>;
    if (!files || files.length === 0)
        return (
            <div className="text-sm text-[#a8b0bb] py-6 text-center">
                No log files available
            </div>
        );

    const q = filter.trim().toLowerCase();
    const visibleFiles = q
        ? files.filter((f) => f.name.toLowerCase().includes(q))
        : files;

    return (
        <div className="flex gap-3 flex-1 min-h-0">
            {/* Left: searchable, scrollable file list */}
            <div className="w-80 flex-shrink-0 flex flex-col min-h-0 border-r border-[#2d2d2d] pr-2">
                <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder={`Filter ${files.length} files…`}
                    className="flex-shrink-0 mb-2 px-2 py-1 rounded text-xs bg-[#0f141a] border border-[#2d2d2d] text-[#d8dde2] placeholder:text-[#6b7480] focus:outline-none focus:border-[#0d6efd]"
                />
                <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                    <table className="w-full text-xs font-mono border-collapse">
                        <thead className="sticky top-0 bg-[#161c22] text-[#6b7480] text-left">
                            <tr>
                                <th className="px-2 py-1 font-normal">Name</th>
                                <th className="px-2 py-1 font-normal">Date</th>
                                <th className="px-2 py-1 font-normal">Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleFiles.map((f) => {
                                const parsed = parseLogName(f.name);
                                return (
                                    <tr
                                        key={f.name}
                                        onClick={() => setSelected(f.name)}
                                        title={`${f.name} — ${formatSize(f.size)}${
                                            f.lastModified
                                                ? ` — ${utcStamp(f.lastModified, true)}`
                                                : ''
                                        }`}
                                        className={`cursor-pointer transition-colors ${
                                            selected === f.name
                                                ? 'bg-[#0d6efd] text-white'
                                                : 'text-[#aaa] hover:bg-[#1a2126] hover:text-white'
                                        }`}
                                    >
                                        <td className="px-2 py-1 truncate max-w-[8rem]">
                                            {parsed.name}
                                        </td>
                                        <td className="px-2 py-1 whitespace-nowrap">
                                            {parsed.date}
                                        </td>
                                        <td className="px-2 py-1 whitespace-nowrap">
                                            {parsed.time}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {visibleFiles.length === 0 && (
                        <div className="text-xs text-[#6b7480] py-2 px-1">
                            No match
                        </div>
                    )}
                </div>
            </div>

            {/* Right: log content */}
            <div className="flex-1 min-w-0 flex flex-col min-h-0">
                {selected ? (
                    <LogContent macAddress={macAddress} fileName={selected} />
                ) : (
                    <div className="text-sm text-[#a8b0bb] m-auto">
                        Select a log file
                    </div>
                )}
            </div>
        </div>
    );
}

function LogContent({
    macAddress,
    fileName,
}: {
    macAddress: string;
    fileName: string;
}) {
    const { data: content, isLoading, error } = useHubLog(macAddress, fileName);
    const outRef = useRef<HTMLPreElement>(null);
    const stickyRef = useRef<HTMLDivElement>(null);

    const { html, lines, startBlockIndices } = useMemo(
        () => buildHtml(content ?? ''),
        [content],
    );

    // Sticky "current START block" header — mirrors the reference viewer.
    useEffect(() => {
        const out = outRef.current;
        const stickyEl = stickyRef.current;
        if (!out || !stickyEl) return;

        const update = () => {
            if (!startBlockIndices.length) {
                stickyEl.style.display = 'none';
                return;
            }
            const spans = Array.from(
                out.querySelectorAll('[data-start]'),
            ) as HTMLElement[];
            const outTop = out.getBoundingClientRect().top;
            const visible = spans.filter(
                (s) => s.getBoundingClientRect().top <= outTop + 2,
            );
            const current = visible.length ? visible[visible.length - 1] : null;
            const idx = current
                ? parseInt(current.dataset.start as string, 10)
                : startBlockIndices[0];
            const nextIdx =
                startBlockIndices.find((i) => i > idx) ?? lines.length;
            const block = lines.slice(idx, nextIdx);
            const headerBlock = lines.slice(idx, idx + 20);
            let blockShutdown: string | null = null;
            let blockWakeup: string | null = null;
            for (const l of block) {
                if (/↳ Shutdown time set:/.test(l)) blockShutdown = l;
                if (/↳ Wake-up time set:/.test(l)) blockWakeup = l;
            }
            const picked = [
                headerBlock.find((l) => /={3,}.*START.*={3,}/.test(l)),
                headerBlock.find((l) => /Startup reason:/.test(l)),
                headerBlock.find((l) =>
                    /={3,}.*(CONFIG MODE|DEFAULT MODE|WITTYPI NEWLY POWERED MODE).*={3,}/.test(
                        l,
                    ),
                ),
                blockShutdown,
                blockWakeup,
            ].filter(Boolean) as string[];
            stickyEl.innerHTML = picked.map((l) => escapeHtml(l)).join('\n');
            stickyEl.style.display = picked.length ? 'block' : 'none';
        };

        out.addEventListener('scroll', update);
        update();
        // Jump to the tail (latest lines) on load, like the reference viewer.
        out.scrollTop = out.scrollHeight;
        return () => out.removeEventListener('scroll', update);
    }, [html, lines, startBlockIndices]);

    if (isLoading)
        return (
            <Skeleton className="w-full flex-1 min-h-0 mt-2 bg-[#1e242a]" />
        );
    if (error)
        return (
            <div className="text-red-400 text-sm mt-2">{error.toString()}</div>
        );

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Toolbar */}
            <div className="flex items-center gap-2 py-2 text-xs text-[#888]">
                <span>
                    {lines.length.toLocaleString()} lines —{' '}
                    {formatSize((content ?? '').length)}
                </span>
                <span className="ml-auto font-mono text-[#aaa]">{fileName}</span>
            </div>

            {/* Sticky current-block header */}
            <div
                ref={stickyRef}
                style={{ display: 'none' }}
                className="bg-[#1a2a1a] text-[#6fcf6f] text-[0.78rem] px-3 py-1 border-b border-[#2a4a2a] font-mono whitespace-pre-wrap flex-shrink-0"
            />

            {/* Log body — always no-wrap so long lines scroll left/right. */}
            <pre
                ref={outRef}
                style={{ whiteSpace: 'pre' }}
                className="flex-1 min-h-0 overflow-auto bg-[#212529] text-[#ccc] text-[0.78rem] font-mono p-4 m-0"
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
}
